import _extends from "@babel/runtime-corejs3/helpers/extends";
import * as _ from "../../../../util/lodash";
import React, { useState, useEffect, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import ReactQuill, { Quill } from 'react-quill';
import 'quill-mention'; //DO NOT remove this line, it will break the mention functionality

import { richTextToolbar } from "../../../../enums";
import CustomMentionBlot from "./custom-mention-blot"; //DO NOT remove this line, it has to include the custom mention blot functionality

import CustomScrollBlot from "./custom-scroll-blot";
import { colorProps } from "./custom-color-picker";
import QuillToolbar from "./quill-toolbar";
import Utils from "./utils";
Quill.register(Quill["import"]('attributors/style/align'), true); //overwriting quill align classes to use inline styles (instead of class="ql-align ql-align-center" we should get style="text-align: center")

Quill.register(CustomMentionBlot, true);
Quill.register(CustomScrollBlot, true);
Quill.prototype.getBounds = Utils.getBounds;
/**
 * quill themes
 */

var quillTheme = {
  BUBBLE: 'bubble',
  //this theme moves the toolbar into a tooltip
  SNOW: 'snow' //please note, 'snow' is NOT styled, just listing it for you to know about it's existence. This theme would put the toolbar on the top of the editor.

};
/**
 * Quill editor component with custom toolbar and custom color picker components
 * Also handles mentions and adds props to set toolbar options, placeholder, read only, multiline
 */

var QuillEditor = function QuillEditor(p) {
  var _useRef;

  var value = p.value,
      useMentions = p.useMentions,
      mentionList = p.mentionList,
      toolbarOptions = p.toolbarOptions,
      placeholder = p.placeholder,
      readOnly = p.readOnly,
      isMultiline = p.isMultiline,
      onChange = p.onChange,
      onBlur = p.onBlur,
      onFocus = p.onFocus,
      bounds = p.bounds;
  var quillRef = useRef(null);
  var quillId = useRef(Utils.createQuillId()); //toolbar and editor has to be linked through an id, otherwise will NOT work when having multiple instances of the editor.

  var _useState = useState({}),
      selectionColorStyles = _useState[0],
      setSelectionColorStyles = _useState[1]; //when text is being selected in the editor, this state var will hold the selected texts color/background color


  var _useState2 = useState(value),
      editorHtml = _useState2[0],
      setEditorHtml = _useState2[1]; //will hold the edited text in HTML format, as supplied by the quill editor(or from the outside prop)


  var pickersVisibilityRef = useRef((_useRef = {}, _useRef[colorProps.COLOR] = false, _useRef[colorProps.BACKGROUND] = false, _useRef));
  var quillModuleDataRef = useRef(); //current mention list reference. This is needed, cause the quill mention plugin can't get the values in any other way.

  useEffect(function () {
    quillModuleDataRef.current = {
      mentionList: useMentions ? mentionList : [],
      isMultiline: isMultiline
    };
  }, [mentionList, useMentions, isMultiline]); //changing value from outside needs to update the html

  useEffect(function () {
    if (value !== editorHtml) {
      setEditorHtml(value);
    }
  }, [value]); //on component mount

  useEffect(function () {
    //quill isn't having any prop that could set the placeholder of the link editor, so we need to get into the rendered DOM elements and set it like below
    quillRef.current.getEditingArea().querySelector('.ql-tooltip .ql-tooltip-editor input').setAttribute('data-link', 'https://');
  }, []); //handles quill change, sets the internally used html content and also calls onChange

  var handleChange = function handleChange(content, delta, source, editor) {
    var value = Utils.cleanContent(content, isMultiline);
    setEditorHtml(value);

    if (source === Quill.sources.USER && editorHtml !== value) {
      onChange(value);
    }
  }; //handler to be called when the text selection changes in the editor
  //it will get the selected texts color/background and store them in a state variable for usage with the toolbar


  var onChangeSelection = useRef( //when selection changes too many times, just take the last one
  _.debounce(function (selection) {
    if (selection) {
      setSelectionColorStyles(_.pick(quillRef.current.getEditor().getFormat(selection), colorProps.ALL)); //we only care about this two formats
    }
  }, 200)); //handler function for color change called by the toolbar

  var onChangeColor = function onChangeColor(_ref) {
    var colorProp = _ref.colorProp,
        value = _ref.value;

    if (quillRef.current) {
      var editor = quillRef.current.getEditor();
      var scrollTop = editor.scrollingContainer.scrollTop;
      editor.format(colorProp, value, 'user'); //applies the color/background to the selected text

      editor.scrollingContainer.scrollTop = scrollTop;
    }
  }; //we need to disable selection in the editor while the color pickers are in the 'open' state, otherwise the editor listens to the click events over the color popup and will deselect the text/close the toolbar


  var onColorPickerVisibilityChange = function onColorPickerVisibilityChange(colorProp, visible) {
    var _extends2;

    //new visibility
    var visibility = _extends({}, pickersVisibilityRef.current, (_extends2 = {}, _extends2[colorProp] = visible, _extends2));

    pickersVisibilityRef.current = visibility;

    if (quillRef && quillRef.current) {
      var contentEditable = !Utils.colorPickerIsVisible(visibility);
      quillRef.current.getEditor().root.setAttribute('contenteditable', contentEditable); //while the color pickers are visible, the editor should NOT be editable
    }
  };

  return /*#__PURE__*/React.createElement("div", {
    id: quillId.current + "-editor",
    className: "ck-quill-editor-wrapper"
  }, /*#__PURE__*/React.createElement(QuillToolbar, {
    toolbarId: quillId.current + "-toolbar",
    toolbarOptionsObj: Utils.toolbarOptionsArrToObj(toolbarOptions),
    onChangeColor: onChangeColor,
    selectionColorStyles: selectionColorStyles,
    onColorPickerVisibilityChange: onColorPickerVisibilityChange
  }), /*#__PURE__*/React.createElement(ReactQuill, {
    ref: quillRef,
    readOnly: readOnly,
    placeholder: placeholder,
    theme: quillTheme.BUBBLE,
    onChange: handleChange,
    onChangeSelection: onChangeSelection.current,
    value: editorHtml,
    modules: Utils.getQuillModules(quillId.current + "-toolbar", quillModuleDataRef),
    formats: QuillEditor.formats,
    onBlur: onBlur,
    onFocus: onFocus,
    bounds: bounds || "#" + quillId.current + "-editor"
  }));
};

QuillEditor.propTypes = {
  value: PropTypes.string,
  useMentions: PropTypes.bool,
  mentionList: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.string,
    fullText: PropTypes.string
  })),
  placeholder: PropTypes.string,
  toolbarOptions: PropTypes.arrayOf(PropTypes.oneOf(richTextToolbar.ALL)),
  readOnly: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  isMultiline: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func
};
QuillEditor.defaultProps = {
  value: '',
  useMentions: false,
  mentionList: [],
  placeholder: '',
  toolbarOptions: richTextToolbar.ALL,
  readOnly: false,
  isMultiline: true,
  onChange: _.noop,
  onBlur: _.noop,
  onFocus: _.noop
};
/*
 * Quill editor formats. If you don't list them, the editor will REMOVE the specific formats from the html!!!
 * See https://quilljs.com/docs/formats/
 */

QuillEditor.formats = ['header', 'font', 'size', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'bullet', 'indent', 'link', 'script', 'color', 'background', 'align', 'text-align', 'mention'];
export default /*#__PURE__*/memo(QuillEditor);
//# sourceMappingURL=index.js.map